import uuid from 'uuid';
import isEmpty from 'lodash/isEmpty';
import { argumentTypes } from '../config';
import { automationMethodOptions, SCOPE_ALL_CLIENTS } from '../constants';

export const getDefaultArgumentData = customOrder => ({
  name: '',
  description: '',
  order: customOrder || 1,
  type: argumentTypes[0],
  is_required: true,
  id: uuid.v4(),
  type_data: []
});

export const getDefaultExternalReplyData = () => ({
  description: '',
  type: 'external',
  is_resolution: false
});

export const getDefaultInternalReplyData = () => ({
  description: '',
  type: 'internal',
  is_resolution: false
});

export const getDefaultReplyData = () => [
  getDefaultExternalReplyData(),
  getDefaultInternalReplyData()
];

export const getFullReplyData = existingReplies => {
  const externalReply = existingReplies.find(reply => reply.type === 'external');
  const internalReply = existingReplies.find(reply => reply.type === 'internal');
  return [
    externalReply || getDefaultExternalReplyData(),
    internalReply || getDefaultInternalReplyData()
  ];
};

export const getDefaultHeaderData = () => ({
  id: uuid.v4(),
  key: '',
  value: ''
});

export const getDefaultAutomationData = () => ({
  url: '',
  method: automationMethodOptions[0],
  headers: [getDefaultHeaderData()]
});

export const getDefaultVariationData = () => ({
  is_default: true,
  visibility_scope: SCOPE_ALL_CLIENTS,
  is_active: true,
  clients: [],
  company_types: [],
  contact_types: [],
  approval: {
    id: null,
    name: 'Approval for: Default',
    is_active: false,
    contact_types: []
  },
  name: 'Default',
  arguments: [getDefaultArgumentData()],
  replies: getDefaultReplyData(),
  ...getDefaultAutomationData()
});

const parseArgumentTypeData = typeData => {
  try {
    if (Array.isArray(typeData)) {
      return typeData.map(value => ({ value, id: uuid.v4() }));
    }
    if (typeData && typeof typeData === 'string') {
      return JSON.parse(typeData).map(value => ({ value, id: uuid.v4() }));
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const normalizeIntentData = intent => {
  const { variations } = intent;
  return {
    id: intent.id,
    name: intent.name,
    description: intent.description,
    is_active: intent.is_active,
    time_saved: intent.time_saved ?? 30,
    variations: variations.map(variation => {
      const variationArguments = variation.arguments.map(arg => ({
        ...arg,
        type: argumentTypes.find(type => type.value === arg.type) || null,
        type_data: parseArgumentTypeData(arg.type_data)
      }));

      const replies = variation.replies?.length
        ? getFullReplyData(variation.replies)
        : getDefaultReplyData();

      const automationHeaders = isEmpty(variation.headers)
        ? [getDefaultHeaderData()]
        : Object.entries(variation.headers).map(([key, value]) => ({
            id: uuid.v4(),
            key,
            value
          }));

      const automationMethod = variation.method
        ? automationMethodOptions.find(method => method.value === variation.method)
        : automationMethodOptions[0];

      return {
        ...getDefaultVariationData(),
        ...variation,
        arguments: variationArguments,
        replies,
        headers: automationHeaders,
        method: automationMethod
      };
    })
  };
};

export const normalizeTemplateIntentData = template => {
  const variationArguments = template.arguments.map(arg => {
    return {
      ...arg,
      id: uuid.v4(),
      type: argumentTypes.find(type => type.value === arg.type) || null,
      type_data: parseArgumentTypeData(arg.type_data)
    };
  });

  const replies = template.replies?.length
    ? getFullReplyData(template.replies)
    : getDefaultReplyData();

  const automationHeaders = isEmpty(template.headers)
    ? [getDefaultHeaderData()]
    : Object.entries(template.headers).map(([key, value]) => ({
        id: uuid.v4(),
        key,
        value
      }));

  const automationMethod = template.method
    ? automationMethodOptions.find(method => method.value === template.method)
    : automationMethodOptions[0];

  return {
    id: template.id,
    name: template.name,
    description: template.description,
    is_active: true,
    time_saved: template.time_saved ?? 30,
    variations: [
      {
        ...getDefaultVariationData(),
        arguments: variationArguments,
        replies,
        headers: automationHeaders,
        method: automationMethod
      }
    ]
  };
};
